import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { TrendingUp } from 'lucide-react';
import type { TrendPoint } from '../../../tutor/types/tutorAnalytics';

interface TutorTrendChartProps {
  trend: TrendPoint[];
}

export const TutorTrendChart: React.FC<TutorTrendChartProps> = ({ trend }) => {
  if (trend.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-app-text dark:text-white flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Performance Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64 flex items-center justify-center text-app-text-secondary dark:text-gray-400">
            No trend data yet
          </div>
        </CardContent>
      </Card>
    );
  }

  // Chart dimensions and margins
  const width = 400;
  const height = 200;
  const margin = { top: 20, right: 20, bottom: 40, left: 50 };

  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  // Calculate scales
  const dates = trend.map(d => new Date(d.date).getTime());
  const values = trend.map(d => d.overall_avg);

  const minDate = Math.min(...dates);
  const maxDate = Math.max(...dates);
  const minValue = Math.min(...values);
  const maxValue = Math.max(...values);

  // Add some padding to the value range
  const valueRange = maxValue - minValue;
  const valuePadding = valueRange * 0.1 || 10; // minimum padding if all values are same
  const yMin = Math.max(0, minValue - valuePadding);
  const yMax = maxValue + valuePadding;

  const xScale = (date: number) => {
    if (trend.length === 1) return chartWidth / 2;
    return ((date - minDate) / (maxDate - minDate)) * chartWidth;
  };

  const yScale = (value: number) => {
    return chartHeight - ((value - yMin) / (yMax - yMin)) * chartHeight;
  };

  // Generate path for the line
  const pathData = trend.map((point, index) => {
    const x = xScale(new Date(point.date).getTime());
    const y = yScale(point.overall_avg);
    return `${index === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');

  // Generate points for circles
  const points = trend.map((point) => ({
    x: xScale(new Date(point.date).getTime()),
    y: yScale(point.overall_avg),
    value: point.overall_avg,
    date: new Date(point.date).toLocaleDateString(),
  }));

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg font-semibold text-app-text dark:text-white flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Performance Trend
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-64">
          <svg width={width} height={height} className="w-full h-full">
            {/* Grid lines */}
            <defs>
              <pattern id="grid" width="40" height="20" patternUnits="userSpaceOnUse">
                <path d="M 40 0 L 0 0 0 20" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-gray-200 dark:text-gray-700" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#grid)" />

            {/* Y-axis labels */}
            <text x={margin.left - 10} y={margin.top} textAnchor="end" className="text-xs fill-app-text-secondary dark:fill-gray-400">
              {yMax.toFixed(0)}%
            </text>
            <text x={margin.left - 10} y={chartHeight + margin.top} textAnchor="end" className="text-xs fill-app-text-secondary dark:fill-gray-400">
              {yMin.toFixed(0)}%
            </text>

            {/* Line */}
            <path
              d={pathData}
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              className="text-blue-500 dark:text-blue-400"
            />

            {/* Data points */}
            {points.map((point, index) => (
              <circle
                key={index}
                cx={point.x + margin.left}
                cy={point.y + margin.top}
                r="4"
                className="fill-blue-500 dark:fill-blue-400 stroke-white dark:stroke-gray-800"
                strokeWidth="2"
              />
            ))}

            {/* X-axis labels */}
            {trend.length > 1 && trend.map((point, index) => {
              const x = xScale(new Date(point.date).getTime()) + margin.left;
              if (index === 0 || index === trend.length - 1 || index === Math.floor(trend.length / 2)) {
                return (
                  <text
                    key={index}
                    x={x}
                    y={height - margin.bottom + 15}
                    textAnchor="middle"
                    className="text-xs fill-app-text-secondary dark:fill-gray-400"
                  >
                    {new Date(point.date).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </text>
                );
              }
              return null;
            })}
          </svg>
        </div>
      </CardContent>
    </Card>
  );
};